import React, { createContext, useCallback, useReducer } from 'react';

import {
  initiateSilentRedirect,
  initiateLogin,
  logout as authLogout,
} from './auth-service';
import { isAuthEnabled } from './config';
import type { AuthState, JwtPayload, TokenSet } from './types';

// Pages that must never trigger a silent redirect (would cause an infinite loop)
const AUTH_PAGES = ['/login', '/auth/callback', '/auth/error'];

type AuthAction =
  | { type: 'LOGIN_START' }
  | { payload: { tokens: TokenSet; user: JwtPayload }; type: 'LOGIN_SUCCESS' }
  | { payload: string; type: 'LOGIN_ERROR' }
  | { type: 'LOGOUT' };

interface AuthContextValue extends AuthState {
  login: (returnUrl?: string) => Promise<void>;
  logout: () => void;
  setAuthenticated: (tokens: TokenSet, user: JwtPayload) => void;
}

// Mock identity used when REACT_APP_AUTH_ENABLED=false (dev only).
const MOCK_USER: JwtPayload = {
  aud: 'local',
  email: 'dev@local',
  exp: Math.floor(Date.now() / 1000) + 86400,
  iat: Math.floor(Date.now() / 1000),
  iss: 'local',
  sub: 'dev-user',
  token_use: 'id',
};
const MOCK_TOKENS: TokenSet = {
  accessToken: 'mock-access-token',
  expiresAt: Math.floor(Date.now() / 1000) + 86400,
  idToken: 'mock-id-token',
};

const initialState: AuthState = isAuthEnabled
  ? { error: null, isAuthenticated: false, isLoading: false, tokens: null, user: null }
  : { error: null, isAuthenticated: true, isLoading: false, tokens: MOCK_TOKENS, user: MOCK_USER };

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, error: null, isLoading: true };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        error: null,
        isAuthenticated: true,
        isLoading: false,
        tokens: action.payload.tokens,
        user: action.payload.user,
      };
    case 'LOGIN_ERROR':
      return {
        ...state,
        error: action.payload,
        isAuthenticated: false,
        isLoading: false,
        tokens: null,
        user: null,
      };
    case 'LOGOUT':
      return { ...initialState };
    default:
      return state;
  }
}

export const AuthContext = createContext<AuthContextValue>({
  ...initialState,
  login: async () => {},
  logout: () => {},
  setAuthenticated: () => {},
});

export const AuthProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const login = useCallback(async (returnUrl?: string) => {
    dispatch({ type: 'LOGIN_START' });
    await initiateLogin(returnUrl);
  }, []);

  const logout = useCallback(() => {
    dispatch({ type: 'LOGOUT' });
    if (isAuthEnabled) {
      authLogout();
    }
  }, []);

  const setAuthenticated = useCallback(
    (tokens: TokenSet, user: JwtPayload) => {
      dispatch({ payload: { tokens, user }, type: 'LOGIN_SUCCESS' });
    },
    [],
  );

  return (
    <AuthContext.Provider
      value={{ ...state, login, logout, setAuthenticated }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Call this from AuthGuard when the user is not authenticated.
 * Triggers a silent round-trip to Cognito (prompt=none).
 * The current URL is saved as returnUrl so the user lands back
 * where they were after a successful silent auth.
 */
export function triggerSilentRedirect(): void {
  const currentPath = window.location.pathname + window.location.search;
  if (AUTH_PAGES.includes(window.location.pathname)) return;
  initiateSilentRedirect(currentPath);
}
